import * as libphonenumber from 'libphonenumber-js';
import { PHONE_COUNTRIES } from './phoneCountriesData';

export const DEFAULT_COUNTRY = 'CH';

/** Neighbours / frequent Ticketack markets first in the country select. */
export const PREFERRED_COUNTRIES = [
    'CH', 'FR', 'DE', 'IT', 'BE', 'AT', 'LI', 'LU', 'GB', 'US', 'ES', 'PT', 'NL',
];

/** Spacing groups for national numbers (fallback when AsYouType is unavailable). */
const NATIONAL_GROUPS = {
    CH: [2, 3, 2, 2],
    FR: [1, 2, 2, 2, 2],
    DE: [3, 3, 3],
    IT: [3, 3, 3],
    BE: [3, 2, 2, 2],
    AT: [3, 3, 4],
    GB: [4, 6],
    US: [3, 3, 4],
    ES: [3, 2, 2, 2],
    PT: [3, 3, 3],
    NL: [2, 3, 2, 2],
};

function lib_api() {
    const root = libphonenumber && typeof libphonenumber === 'object' ? libphonenumber : {};
    const nested = root.default && typeof root.default === 'object' ? root.default : {};
    return { ...root, ...nested };
}

function try_parse_phone(candidate) {
    const api = lib_api();
    const parser = api.parsePhoneNumberFromString
        || api.parsePhoneNumber
        || (typeof api.default === 'function' ? api.default : null);
    if (typeof parser !== 'function' || !candidate)
        return undefined;
    try {
        return parser(candidate);
    } catch (e) {
        return undefined;
    }
}

function try_as_you_type(country, international) {
    const { AsYouType } = lib_api();
    if (typeof AsYouType !== 'function')
        return null;
    try {
        return new AsYouType(country).input(international);
    } catch (e) {
        return null;
    }
}

const country_by_iso = PHONE_COUNTRIES.reduce((acc, row) => {
    acc[row.iso] = row;
    return acc;
}, {});

const codes_longest_first = (() => {
    const by_code = {};
    PHONE_COUNTRIES.forEach(({ iso, code }) => {
        if (!by_code[code])
            by_code[code] = [];
        by_code[code].push(iso);
    });
    return Object.keys(by_code)
        .sort((a, b) => b.length - a.length)
        .map((code) => ({ code, isos: by_code[code] }));
})();

/**
 * Digits / + / 00… → E.164 candidate with a leading +.
 * Engine often stores international numbers without '+'.
 */
export function internationalize_phone(raw) {
    const compact = String(raw || '').trim().replace(/[^\d+]/g, '').replace(/^00/, '+');
    if (!compact)
        return '';
    if (compact.startsWith('+'))
        return compact;
    return `+${compact}`;
}

export function calling_code_prefix(country) {
    const row = country_by_iso[country];
    if (!row)
        throw new Error(`Unknown phone country: ${country}`);
    return `+${row.code}`;
}

function pick_country_for_code(isos, preferred = PREFERRED_COUNTRIES) {
    const hit = preferred.find((iso) => isos.includes(iso));
    return hit || isos[0];
}

function group_digits(digits, groups) {
    if (!groups || !groups.length)
        return digits;
    const parts = [];
    let rest = digits;
    for (const size of groups) {
        if (!rest)
            break;
        parts.push(rest.slice(0, size));
        rest = rest.slice(size);
    }
    if (rest)
        parts.push(rest);
    return parts.join(' ');
}

/**
 * Format national digits for the number input (spaces, no country prefix).
 */
export function format_national_display(country, national_digits) {
    const digits = String(national_digits || '').replace(/\D/g, '');
    if (!digits)
        return '';

    const code = country_by_iso[country]?.code;
    if (code) {
        const via_lib = try_as_you_type(country, `+${code}${digits}`);
        if (via_lib)
            return via_lib.replace(new RegExp(`^\\+${code}\\s*`), '');
    }

    return group_digits(digits, NATIONAL_GROUPS[country]);
}

/**
 * Parse a stored phone into country ISO + national digits + display string.
 * Accepts +33…, 0033…, and bare 33… (Engine often stores without +).
 */
export function parse_phone_field_value(raw, preferred = PREFERRED_COUNTRIES) {
    const international = internationalize_phone(raw);
    if (!international || international === '+') {
        return { country: DEFAULT_COUNTRY, national: '', display: '' };
    }

    const parsed = try_parse_phone(international);
    if (parsed?.country && country_by_iso[parsed.country]) {
        return {
            country: parsed.country,
            national: parsed.nationalNumber,
            display: format_national_display(parsed.country, parsed.nationalNumber),
        };
    }

    const digits = international.slice(1);
    for (const { code, isos } of codes_longest_first) {
        if (!digits.startsWith(code))
            continue;
        const country = pick_country_for_code(isos, preferred);
        const national = digits.slice(code.length);
        return {
            country,
            national,
            display: format_national_display(country, national),
        };
    }

    return {
        country: DEFAULT_COUNTRY,
        national: digits,
        display: format_national_display(DEFAULT_COUNTRY, digits),
    };
}

/**
 * Build the value stored in the form / API.
 * Empty national → '' (cleared), unless keep_prefix_when_empty (country-only change).
 */
export function serialize_phone_field_value(country, national_digits, { keep_prefix_when_empty = false } = {}) {
    const digits = String(national_digits || '').replace(/\D/g, '');
    const prefix = calling_code_prefix(country);
    if (!digits)
        return keep_prefix_when_empty ? prefix : '';
    return `${prefix}${digits}`;
}

export function flag_emoji(iso) {
    return String(iso || '')
        .toUpperCase()
        .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

/**
 * Options for the country calling-code <select>.
 * @param {string} lang
 * @param {(iso: string) => string} country_name  localized name lookup
 */
export function phone_country_options(lang, country_name) {
    const preferred = PREFERRED_COUNTRIES.filter((iso) => country_by_iso[iso]);
    const preferred_set = new Set(preferred);
    // Resolve localized names once — sort comparators must stay O(1).
    const name_by_iso = new Map(
        PHONE_COUNTRIES.map((row) => [row.iso, country_name(row.iso) || row.iso]),
    );
    const rest = PHONE_COUNTRIES
        .map((row) => row.iso)
        .filter((iso) => !preferred_set.has(iso))
        .sort((a, b) => {
            const na = name_by_iso.get(a) || a;
            const nb = name_by_iso.get(b) || b;
            return na.localeCompare(nb, lang || 'fr');
        });

    return [...preferred, ...rest].map((iso) => {
        const code = calling_code_prefix(iso);
        const name = name_by_iso.get(iso) || iso;
        return {
            iso,
            code,
            name,
            text: `${flag_emoji(iso)} ${code}`,
            title: `${name} (${code})`,
        };
    });
}

export function is_possible_phone(country, national_digits) {
    const digits = String(national_digits || '').replace(/\D/g, '');
    if (!digits)
        return true;
    const candidate = serialize_phone_field_value(country, digits);
    const phone = try_parse_phone(candidate);
    if (phone && typeof phone.isPossible === 'function')
        return phone.isPossible();
    // Without libphonenumber (Jest), accept 4–15 national digits.
    return digits.length >= 4 && digits.length <= 15;
}
